
import type { PropType } from 'vue'
import type { EpbBaseInputSize } from '../epb-base-input/epb-base-input.types'
import type { EpbTextboxShape } from '../epb-textbox/epb-textbox.types'
import type { EpbKeywordsItemId } from './epb-keywords.types'
import { computed, defineComponent, getCurrentInstance, nextTick, onMounted, ref, watch } from 'vue'
import { ColorEnum } from '../../../enums'
import { ArrayUtils } from '../../../utils/array.utils'
import { StringUtils } from '../../../utils/string.utils'
import EpbBaseInput from '../epb-base-input/epb-base-input.component.vue'
import EpbInputHelp from '../epb-input-help/epb-input-help.component.vue'
import EpbInputLabel from '../epb-input-label/epb-input-label.component.vue'
import EpbPill from '../epb-pill/epb-pill.component.vue'
import EpbText from '../epb-text/epb-text.component.vue'
import EpbTextarea from '../epb-textarea/epb-textarea.component.vue'
import EpbTextbox from '../epb-textbox/epb-textbox.component.vue'

export default defineComponent({
  name: 'EpbKeywords',
  components: {
    EpbBaseInput,
    EpbInputHelp,
    EpbInputLabel,
    EpbPill,
    EpbText,
    EpbTextarea,
    EpbTextbox,
  },
  props: {
    value: {
      type: Array as PropType<Array<string>>,
      required: true,
    },
    help: {
      type: String,
      default: undefined,
    },
    helpIcon: {
      type: String,
      default: undefined,
    },
    label: {
      type: String,
      default: undefined,
    },
    icon: {
      type: String,
      default: undefined,
    },
    maxlength: {
      type: Number,
      default: undefined,
    },
    size: {
      type: String as PropType<EpbBaseInputSize>,
      default: 'md',
    },
    shape: {
      type: String as PropType<EpbTextboxShape>,
      default: 'rounded',
    },
    showCounter: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
    background: Boolean,
    required: Boolean,
    readonly: Boolean,
    error: {
      type: String,
      default: undefined,
    },
    info: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    color: {
      type: String,
      default: ColorEnum.NEUTRAL_25,
    },
    pillSize: {
      type: String,
      default: 'small',
    },
    inputComponent: {
      type: String as PropType<'epb-textbox' | 'epb-textarea'>,
      default: 'epb-textbox',
    },
    separators: {
      type: Array as PropType<Array<string>>,
      default: () => [';', ',', '\n'],
    },
    validateFn: {
      type: Function as PropType<(ids: string[]) => Promise<EpbKeywordsItemId[]>>,
      default: (ids: string[]) => {
        return Promise.resolve(ids.map(id => ({ id, valid: true })))
      },
    },
    toLowerCase: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    input: (value: Array<string>) => {},
    change: (value: Array<string>) => {},
    valid: (value: boolean) => {},
  },
  setup(props, { emit }) {
    const baseClass = 'epb-keywords'

    const ctx = getCurrentInstance()

    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const messages = ctx.proxy.$epb.messages.app

    const inputText = ref('')
    const isLoading = ref<boolean>(false)
    const pillsIds = ref<EpbKeywordsItemId[]>([])
    const keywordIds = computed(() => pillsIds.value.map(({ id }) => id))

    const parsedIds = computed<string[]>(() =>
      StringUtils.split(
        props.toLowerCase ? inputText.value.toLowerCase() : inputText.value,
        props.separators.join(''),
      ),
    )

    const inputHasKeywordsAlreadyAdded = computed<boolean>(
      () =>
        !!pillsIds.value.filter(pillId => parsedIds.value.find(parsedId => parsedId === pillId.id))
          .length,
    )

    const emitValidEvent = (): void => {
      emit('valid', pillsIds.value.length && !pillsIds.value.find(p => !p.valid))
    }

    const changeKeywordIds = (newValue: EpbKeywordsItemId[]): void => {
      pillsIds.value = newValue
      emit('input', keywordIds.value)
      emitValidEvent()
      if (keywordIds.value.length !== props.value.length) {
        emit('change', keywordIds.value)
      }
    }

    const addValue = async (event: Event): Promise<void> => {
      ;(event.target as HTMLInputElement).blur()
      isLoading.value = true

      const idsToAdd = ArrayUtils.removeDuplicates(parsedIds.value)
      const validatedIds = await props.validateFn(idsToAdd)
      const mergedIds = ArrayUtils.addUniqueItems(pillsIds.value, validatedIds, 'id')
      const idsSortedByValid = mergedIds.sort(ArrayUtils.sortBy('valid'))
      changeKeywordIds(idsSortedByValid)
      inputText.value = ''
      isLoading.value = false
      if (event.type !== 'blur') {
        nextTick(() => (event.target as HTMLInputElement).focus())
      }
    }

    const removeValue = (index: number): void => {
      const keywordsIdsCopy = [...pillsIds.value]
      keywordsIdsCopy.splice(index, 1)
      changeKeywordIds(keywordsIdsCopy)
    }

    const validatePillIds = async () => {
      pillsIds.value = props.value.length ? await props.validateFn(props.value) : []
      emitValidEvent()
    }

    watch(
      (): string[] => props.value,
      async (): Promise<void> => {
        if (props.value.length !== keywordIds.value.length) {
          await validatePillIds()
        }
      },
    )

    onMounted(async () => {
      await validatePillIds()
    })

    return {
      addValue,
      baseClass,
      messages,
      isLoading,
      pillsIds,
      inputHasKeywordsAlreadyAdded,
      inputText,
      removeValue,
      validatePillIds,
    }
  },
})
